import { Link, useNavigate, useParams } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import AppLoader from "../../components/AppLoader";
import { usePageLoader } from "../../services/usePageLoader";
import {
  getJobGuidedStep,
  patchJobGuidedChecklist,
  postJobGuidedCompleteSetup,
  type JobChecklistItemDto,
  type JobGuidedStepDto,
} from "../../services/services";
import "../trurecipe/trurecipe-pages.scss";
import "../trurecipe/guided-instructions.scss";
import "./mes-job-guided.scss";

const SAFETY_ALERTS = ["Wear eye protection", "Hot surface — allow 30min cooldown"];

function GaugeCard(props: {
  title: string;
  target: string;
  current: string;
  range: string;
  thumbPct: number;
}) {
  return (
    <div className="tv-guided__gauge">
      <div className="tv-guided__gauge-head">
        <span>{props.title}</span>
        <i className="fa fa-check" aria-hidden />
      </div>
      <div className="tv-guided__gauge-body">
        <div className="tv-guided__gauge-values">
          <div>
            <strong>Target:</strong> {props.target}
          </div>
          <div>
            <strong>Current:</strong>{" "}
            <span className="tv-guided__gauge-current">{props.current}</span>
          </div>
        </div>
        <div className="tv-guided__gauge-track">
          <span
            className="tv-guided__gauge-dot"
            style={{ left: `${props.thumbPct}%` }}
            aria-hidden
          />
        </div>
        <div className="tv-guided__gauge-range">{props.range}</div>
      </div>
    </div>
  );
}

const MesJobGuided = () => {
  const { jobId: jobIdParam, stepIndex: stepParam } = useParams<{
    jobId: string;
    stepIndex: string;
  }>();
  const navigate = useNavigate();
  const { isLoading, runWithLoading } = usePageLoader();
  const [ctx, setCtx] = useState<JobGuidedStepDto | null>(null);

  const jobId = Number(jobIdParam);
  const stepIndex = Number(stepParam);

  const load = useCallback(async () => {
    if (!Number.isFinite(jobId) || jobId <= 0 || !Number.isFinite(stepIndex) || stepIndex <= 0) {
      navigate("/mes-jobs", { replace: true });
      return;
    }
    setCtx(null);
    await runWithLoading(async () => {
      const row = await getJobGuidedStep(jobId, stepIndex);
      if (!row) {
        toast.error("Could not load guided instructions for this step.");
        navigate(`/mes-jobs/${jobId}`, { replace: true });
        return;
      }
      setCtx(row);
    });
  }, [jobId, stepIndex, navigate, runWithLoading]);

  useEffect(() => {
    void load();
  }, [load]);

  const pct = ctx?.checklistProgressPct ?? 0;
  const isComplete = ctx?.status === "Complete";
  const checklist = ctx?.checklist ?? [];
  const totalSteps = ctx?.totalSteps ?? 1;

  const toggleCheck = async (item: JobChecklistItemDto) => {
    if (!ctx || isComplete) return;
    const next = !item.checked;
    try {
      const res = await patchJobGuidedChecklist(jobId, {
        stepIndex: ctx.stepIndex,
        workInstructionId: item.workInstructionId,
        checked: next,
      });
      setCtx((prev) =>
        prev
          ? {
              ...prev,
              checklist: res.checklist,
              checklistProgressPct: res.checklistProgressPct,
              status: res.status,
              workflowStepCompleted: res.workflowStepCompleted,
            }
          : prev,
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to update checklist");
    }
  };

  const markSetupComplete = async () => {
    if (!ctx) return;
    if (!checklist.length) {
      toast.warning("No work instructions on this recipe for this step.");
      return;
    }
    if (!checklist.every((c) => c.checked)) {
      toast.warning("Check all items before marking setup complete.");
      return;
    }
    try {
      await postJobGuidedCompleteSetup(jobId, { stepIndex: ctx.stepIndex });
      toast.success("Step setup marked complete.");
      navigate(`/mes-jobs/${jobId}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to complete setup");
    }
  };

  const prevStep = stepIndex > 1 ? stepIndex - 1 : null;
  const nextStep = stepIndex < totalSteps ? stepIndex + 1 : null;

  const subtitle = useMemo(() => {
    if (!ctx) return "";
    return `${ctx.jobname ?? "Job"} — ${ctx.stepTitle}`;
  }, [ctx]);

  const statusClass = ctx?.status
    ? `tv-job-guided__status tv-job-guided__status--${ctx.status.replace(/\s+/g, "-").toLowerCase()}`
    : "";

  return (
    <div className="tv-mes-v2-page">
      {isLoading && <AppLoader text="Loading guided instructions..." />}
      <div className="tv-guided">
        <Link to={`/mes-jobs/${jobId}`} className="tv-guided__back">
          <i className="fa fa-arrow-left" aria-hidden />
          <span>Back to Job</span>
        </Link>

        <h1 className="tv-guided__title">Guided Work Instructions</h1>
        {subtitle ? <p className="tv-job-guided__subtitle">{subtitle}</p> : null}

        <div className="tv-guided__grid">
          <main className="tv-guided__main">
            <div className="tv-guided__progress-row">
              <span className="tv-guided__step-label">
                Step {stepIndex} of {totalSteps}
                {ctx?.status ? <span className={statusClass}>{ctx.status}</span> : null}
              </span>
              <span className="tv-guided__pct tv-guided__pct--muted">{pct}% Complete</span>
            </div>
            <div
              className="tv-guided__bar"
              role="progressbar"
              aria-valuenow={pct}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div className="tv-guided__bar-fill" style={{ width: `${pct}%` }} />
            </div>

            <div className="tv-guided__ar-status">
              <i className="fa fa-eye tv-guided__ar-eye" aria-hidden />
              <input type="checkbox" className="tv-guided__ar-ck" aria-label="AR glasses connection" />
              <span>AR Glasses Connected</span>
            </div>

            <div className="tv-guided__viewport-wrap">
              <div className="tv-guided__viewport" aria-label="AR camera view placeholder">
                <div className="tv-guided__viewport-inner">
                  <div className="tv-guided__ar-icons">
                    <div className="tv-guided__ar-tile tv-guided__ar-tile--tool">
                      <i className="fa fa-crosshairs" aria-hidden />
                      <span>Tool</span>
                    </div>
                    <div className="tv-guided__ar-tile tv-guided__ar-tile--frame" aria-hidden />
                    <div className="tv-guided__ar-tile tv-guided__ar-tile--zone">
                      <span className="tv-guided__focus-brackets" aria-hidden>
                        <span className="tv-guided__focus-brackets__c tv-guided__focus-brackets__c--tl" />
                        <span className="tv-guided__focus-brackets__c tv-guided__focus-brackets__c--tr" />
                        <span className="tv-guided__focus-brackets__c tv-guided__focus-brackets__c--bl" />
                        <span className="tv-guided__focus-brackets__c tv-guided__focus-brackets__c--br" />
                      </span>
                      <span>Zone</span>
                    </div>
                  </div>
                  <p className="tv-guided__viewport-caption">AR Camera View — Safety Zone Active</p>
                </div>
              </div>
            </div>

            <h2 className="tv-guided__checklist-title">Step checklist</h2>
            {checklist.length === 0 ? (
              <p className="tv-job-guided__empty">
                No work instructions on this recipe. Add them in Recipe detail → Work instructions.
              </p>
            ) : (
              <div className="tv-guided__checklist">
                {checklist.map((c) => {
                  const itemKey =
                    c.checklistItemKey ?? `${stepIndex}-${c.workInstructionId}`;
                  return (
                    <label key={itemKey} className="tv-guided__check">
                      <input
                        type="checkbox"
                        checked={Boolean(c.checked)}
                        disabled={isComplete}
                        onChange={() => void toggleCheck(c)}
                      />
                      {c.label}
                    </label>
                  );
                })}
              </div>
            )}

            <div className="tv-guided__actions">
              <button type="button" className="tv-guided__action tv-guided__action--photo">
                <i className="fa fa-camera" aria-hidden />
                Capture Photo
              </button>
              <button type="button" className="tv-guided__action tv-guided__action--video">
                <i className="fa fa-video-camera" aria-hidden />
                Record Video
              </button>
              <button
                type="button"
                className="tv-guided__action tv-guided__action--done"
                disabled={isComplete || !checklist.length}
                onClick={() => void markSetupComplete()}
              >
                <i className="fa fa-check" aria-hidden />
                Mark Setup Complete
              </button>
            </div>

            <div className="tv-guided__nav">
              <button
                type="button"
                className="tv-guided__nav-btn"
                disabled={!prevStep}
                onClick={() => prevStep && navigate(`/mes-jobs/${jobId}/guided/${prevStep}`)}
              >
                <i className="fa fa-chevron-up" aria-hidden />
                Previous
              </button>
              <button
                type="button"
                className="tv-guided__nav-btn tv-guided__nav-btn--next"
                disabled={!nextStep}
                onClick={() => nextStep && navigate(`/mes-jobs/${jobId}/guided/${nextStep}`)}
              >
                Next
                <i className="fa fa-chevron-down" aria-hidden />
              </button>
            </div>
          </main>

          <aside className="tv-guided__side" aria-label="Live IoT parameters">
            <div className="tv-guided__iot">
              <div className="tv-guided__iot-head">
                <i className="fa fa-wifi" aria-hidden />
                IoT Health
              </div>
              <p className="tv-guided__iot-meta">2 of 3 parameters OK</p>
              <p className="tv-guided__iot-meta tv-guided__iot-meta--muted" style={{ marginTop: 4 }}>
                Last sync: 2 seconds ago
              </p>
              <p className="tv-guided__iot-meta tv-guided__iot-meta--muted" style={{ marginTop: 8 }}>
                Workflow: {ctx?.workflowStepCompleted ?? 0} of {ctx?.workflowStepTotal ?? 0} steps
                complete
              </p>
            </div>

            <div className="tv-guided__alert" role="status">
              <i className="fa fa-exclamation-triangle" aria-hidden />
              <span>
                Parameter Alert: Coolant Pressure drifting — 3.1 bar (target: 3.2)
              </span>
            </div>

            <GaugeCard
              title="Spindle Speed"
              target="1800 RPM"
              current="1805 RPM"
              range="1700 - 1900 RPM"
              thumbPct={52}
            />
            <GaugeCard
              title="Feed Rate"
              target="0.15 mm/rev"
              current="0.152 mm/rev"
              range="0.12 - 0.18 mm/rev"
              thumbPct={58}
            />
            <GaugeCard
              title="Coolant Pressure"
              target="3.2 bar"
              current="3.1 bar"
              range="3 - 3.5 bar"
              thumbPct={28}
            />

            <div className="tv-guided__qc">
              <strong>Quality Checkpoint</strong>
              <span>Auto-validated</span>
            </div>
          </aside>
        </div>

        <section className="tv-guided__safety" aria-label="Safety zone alerts">
          <h2 className="tv-guided__safety-title">
            <i className="fa fa-shield" aria-hidden />
            Safety Zone Alerts
          </h2>
          <div className="tv-guided__safety-list">
            {SAFETY_ALERTS.map((msg) => (
              <div key={msg} className="tv-guided__safety-banner">
                <i className="fa fa-exclamation-triangle" aria-hidden />
                {msg}
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default MesJobGuided;
